import { createStore } from "./store";
import { DEFAULT_FILTERS, filters, settings, decisions, LEN_MAX, type Filters, type HideType } from "./app";
import { goTab } from "./router";
import { partnerSets } from "./partner";
import { collectionById } from "../data/collections";
import { sexIdxFor } from "../data/format";
import { nameKey } from "../data/keys";
import type { CoreData, InsightsData, NameEntry, SexIdx, SexPref } from "../data/types";
import { TAG } from "../data/types";

// ------------------------------------------------------------------ rng

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

// ------------------------------------------------------------------ filtering

export function inDeckFor(e: NameEntry, pref: SexPref): boolean {
  if (pref === "F") return e.g !== "M";
  if (pref === "M") return e.g !== "F";
  return true;
}

/** The name-type filters: kinds of name someone has chosen not to see. */
export function hiddenType(e: NameEntry, hide: HideType[]): boolean {
  for (const h of hide) {
    if (h === "joined" && (e.name.includes("-") || e.name.includes(" "))) return true;
    if (h === "alt" && e.tags & TAG.ALT_SPELLING) return true;
    if (h === "short" && e.tags & TAG.SHORT_FORM) return true;
    if (h === "unisex" && e.tags & TAG.UNISEX) return true;
    if (h === "apostrophe" && e.name.includes("'")) return true;
  }
  return false;
}

let meaningRe: { q: string; re: RegExp } | null = null;
/** whole words from their start: "light" finds "light" and "lights", not "delight" */
function meaningMatches(text: string, q: string): boolean {
  if (meaningRe?.q !== q) meaningRe = { q, re: new RegExp(`\\b${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "i") };
  return meaningRe.re.test(text);
}

let listKeys: { keys: string[]; set: Set<string> } | null = null;
function inList(key: string, keys: string[]): boolean {
  if (listKeys?.keys !== keys) listKeys = { keys, set: new Set(keys) };
  return listKeys.set.has(key);
}

/** What the deck has been narrowed to from Explore (a collection, a year's top 100), if anything. */
export function deckFocus(f: Filters): string | null {
  if (f.collection && f.collection !== "partner") return collectionById(f.collection)?.title ?? "A collection";
  if (f.list) return f.list.title;
  return null;
}

/** Lists in Explore leave out the kinds of name someone has hidden from their deck. */
export function shownInLists(e: NameEntry, f: Filters, pref: SexPref): boolean {
  return inDeckFor(e, pref) && !hiddenType(e, f.hide) && !(e.origin && f.hideOrigins.includes(e.origin));
}

/** Start swiping a set of names from Explore, keeping the kinds of name someone has hidden. */
export function swipeWith(patch: Partial<Filters>): void {
  const f = filters.get();
  filters.set({ ...DEFAULT_FILTERS, hide: f.hide, hideOrigins: f.hideOrigins, ...patch });
  goTab("swipe");
}

export function passes(e: NameEntry, f: Filters, pref: SexPref, core: CoreData): boolean {
  if (!inDeckFor(e, pref)) return false;
  if (f.list && !inList(e.key, f.list.keys)) return false;
  if (f.hide.length && hiddenType(e, f.hide)) return false;
  if (f.hideOrigins.length && e.origin && f.hideOrigins.includes(e.origin)) return false;
  if (f.nameText.trim()) {
    const q = nameKey(f.nameText);
    if (q && !(f.nameMode === "contains" ? e.key.includes(q) : f.nameMode === "ends" ? e.key.endsWith(q) : e.key.startsWith(q)))
      return false;
  }
  if (f.meaningText.trim() && !meaningMatches(`${e.meaning} ${e.originNote}`, f.meaningText.trim())) return false;
  const si: SexIdx = sexIdxFor(e, pref);
  const rank = e.r[si];
  switch (f.pop) {
    case "top100":
      if (!rank || rank > 100) return false;
      break;
    case "top500":
      if (!rank || rank > 500) return false;
      break;
    case "top1000":
      if (!rank || rank > 1000) return false;
      break;
    case "known":
      if (!rank || rank <= 100 || rank > 1000) return false;
      break;
    case "uncommon":
      if (!rank || rank <= 1000 || rank > 5000) return false;
      break;
    case "rare":
      if (rank && rank <= 5000) return false;
      break;
  }
  if (f.trend !== "any") {
    const t = e.t[si];
    if (f.trend === "new") {
      if (!(e.tags & TAG.NEW)) return false;
    } else if (t === null) return false;
    else if (f.trend === "rising" && t < 25) return false;
    else if (f.trend === "falling" && t > -25) return false;
    else if (f.trend === "steady" && (t >= 25 || t <= -25)) return false;
  }
  if (f.character.length) {
    let any = false;
    for (const c of f.character) {
      if (
        (c === "classic" && e.tags & TAG.CLASSIC) ||
        (c === "vintage" && e.tags & TAG.VINTAGE) ||
        (c === "unisex" && e.tags & TAG.UNISEX) ||
        (c === "scotland" && e.tags & TAG.SCOTLAND) ||
        (c === "ni" && e.tags & TAG.NI) ||
        (c === "timeless" && e.tags & TAG.TIMELESS)
      ) {
        any = true;
        break;
      }
    }
    if (!any) return false;
  }
  if (f.letters.length && !f.letters.includes(e.initial)) return false;
  if (e.len < f.lenMin) return false;
  if (f.lenMax < LEN_MAX && e.len > f.lenMax) return false;
  if (f.syl.length && !f.syl.includes(Math.min(e.syl, 4))) return false;
  if (f.origins.length && !(e.origin && f.origins.includes(e.origin))) return false;
  const col = collectionById(f.collection);
  if (col && col.id !== "partner" && !col.test(e, si, core)) return false;
  return true;
}

// ------------------------------------------------------------------ spellings

/**
 * Spellings that sound the same (Sophia, Sofia, Sofiia), from insights.json's families: the deck
 * deals one of each (the most common), and deciding on any spelling retires the others. People
 * find the rest under "Other spellings", and search still finds every one.
 */
const spellingFamily = new Map<string, string>(); // "F:sofia" -> "F:3"
const familyMembers = new Map<string, string[]>(); // "F:3" -> keys, most common first

export function setSpellings(ins: InsightsData | null): void {
  spellingFamily.clear();
  familyMembers.clear();
  decidedFams = null;
  if (!ins) return;
  for (const sx of ["F", "M"] as const)
    ins.families[sx].forEach((row, fi) => {
      const keys: string[] = [];
      for (let i = 2; i < row.length; i += 2) keys.push(String(row[i]));
      if (keys.length < 2) return;
      familyMembers.set(`${sx}:${fi}`, keys);
      for (const k of keys) spellingFamily.set(`${sx}:${k}`, `${sx}:${fi}`);
    });
}

function familyOfKey(key: string, si: SexIdx): string | undefined {
  return spellingFamily.get(`${si === 0 ? "F" : "M"}:${key}`);
}

/** The other spellings of a name the deck deals for them, most common first. */
export function otherSpellings(key: string, si: SexIdx): string[] {
  const fam = familyOfKey(key, si);
  return fam ? familyMembers.get(fam)!.filter((k) => k !== key) : [];
}

let decidedFams: { rev: number; set: Set<string> } | null = null;
/** families with a decision on any of their spellings */
function decidedFamilies(): Set<string> {
  const d = decisions.get();
  if (decidedFams?.rev === d.rev) return decidedFams.set;
  const set = new Set<string>();
  if (spellingFamily.size)
    for (const k in d.map)
      for (const sx of ["F", "M"]) {
        const fam = spellingFamily.get(`${sx}:${k}`);
        if (fam) set.add(fam);
      }
  decidedFams = { rev: d.rev, set };
  return set;
}

// ------------------------------------------------------------------ ordering

/**
 * The open deck's mix, keeping each group's own (weighted) order: of every twelve cards, nine
 * well-known names (the top 1,000), two less common (to 5,000) and one rarity. People who want
 * more rarities choose them under Popularity.
 */
function mixed(list: NameEntry[], pref: SexPref): NameEntry[] {
  const groups: NameEntry[][] = [[], [], []];
  for (const e of list) {
    const r = e.r[sexIdxFor(e, pref)];
    groups[r > 0 && r <= 1000 ? 0 : r > 0 && r <= 5000 ? 1 : 2].push(e);
  }
  const SLOTS = [0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 2];
  const at = [0, 0, 0];
  const out: NameEntry[] = [];
  while (out.length < list.length) {
    const want = SLOTS[out.length % SLOTS.length];
    // a group that's run out hands its turn to the nearest one that hasn't
    const g = [want, 0, 1, 2].find((x) => at[x] < groups[x].length)!;
    out.push(groups[g][at[g]++]);
  }
  return out;
}

/**
 * Popularity-weighted shuffle: popular names come up more often, rare ones still appear.
 * (The open deck then mixes it by popularity band: see mixed().)
 */
function weight(e: NameEntry, si: SexIdx): number {
  const c = e.c[si];
  return c > 0 ? Math.pow(c + 2, 0.9) : 0.3;
}

function order(list: NameEntry[], f: Filters, pref: SexPref, rnd: () => number): NameEntry[] {
  const si = (e: NameEntry) => sexIdxFor(e, pref);
  const byRank = (a: NameEntry, b: NameEntry) => (a.r[si(a)] || 1e9) - (b.r[si(b)] || 1e9) || a.peakRank - b.peakRank;
  switch (f.sort) {
    case "popular":
      return list.sort(byRank);
    case "rarest":
      return list.sort((a, b) => (a.c[si(a)] || 0) - (b.c[si(b)] || 0) || byRank(b, a));
    case "az":
      return list.sort((a, b) => a.name.localeCompare(b.name, "en-GB"));
    case "rising":
      return list.sort((a, b) => (b.t[si(b)] ?? -1e9) - (a.t[si(a)] ?? -1e9));
    case "shuffle": {
      for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(rnd() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
      }
      return list;
    }
    default: {
      // Efraimidis-Spirakis weighted sampling without replacement
      const keyed = list.map((e) => ({ e, k: Math.log(rnd() || 1e-12) / weight(e, si(e)) }));
      keyed.sort((a, b) => b.k - a.k);
      return keyed.map((x) => x.e);
    }
  }
}

// ------------------------------------------------------------------ deck store

export interface DeckState {
  /** entry indices in the order they will be dealt */
  order: number[];
  /** keys to deal before `order` (e.g. a card brought back by undo) */
  front: string[];
  /** how many names match the filters in total (decided or not) */
  matching: number;
  built: number;
}

export const deck = createStore<DeckState>({ order: [], front: [], matching: 0, built: 0 });

export function rebuildDeck(core: CoreData): void {
  const f = filters.get();
  const s = settings.get();
  const partner = partnerSets.get();
  const rnd = mulberry32(s.seed ^ hashString(JSON.stringify(f) + s.sex));
  const partnerOnly = f.collection === "partner";
  const list = core.entries.filter(
    (e) => passes(e, f, s.sex, core) && (!partnerOnly || partner.liked.has(e.key)),
  );
  const matching = list.length;
  // cards held on top (one brought back by undo, or the card that was showing when the deck was
  // rebuilt) stay only while they still belong: choosing boys' names mustn't leave a girl's on top
  const allowed = new Set(list.map((e) => e.key));
  const front = deck.get().front.filter((k) => allowed.has(k));
  // shuffled before anything is left out, so the order stays put when the spellings or a
  // partner's passes arrive a moment later (and the card on screen with it)
  let ordered = order(list, f, s.sex, rnd);
  if (s.hidePartnerNopes && partner.nopes.size) ordered = ordered.filter((e) => !partner.nopes.has(e.key));
  if (spellingFamily.size && !f.list) {
    // one spelling of each name: the most common one that's left after the filters (a spelling
    // your partner liked is dealt too, so you can match on it)
    const best = new Map<string, NameEntry>();
    for (const e of ordered) {
      const si = sexIdxFor(e, s.sex);
      const fam = familyOfKey(e.key, si);
      if (!fam) continue;
      const cur = best.get(fam);
      if (!cur || e.c[si] > cur.c[sexIdxFor(cur, s.sex)]) best.set(fam, e);
    }
    ordered = ordered.filter((e) => {
      const fam = familyOfKey(e.key, sexIdxFor(e, s.sex));
      return !fam || best.get(fam) === e || partner.liked.has(e.key);
    });
  }
  if (f.sort === "smart" && !f.collection && !f.list) {
    ordered = mixed(ordered, s.sex);
    // a fresh start opens with familiar names before the mix kicks in
    if (Object.keys(decisions.get().map).length < 30) {
      const opener = ordered
        .filter((e) => {
          const r = e.r[sexIdxFor(e, s.sex)];
          return r > 0 && r <= 500;
        })
        .slice(0, 20);
      if (opener.length) {
        const set = new Set(opener);
        ordered = [...opener, ...ordered.filter((e) => !set.has(e))];
      }
    }
  }
  if (s.partnerFirst && partner.liked.size && !partnerOnly) {
    // weave names your partner liked into the next few cards (every third card)
    const mine = decisions.get().map;
    const picks = ordered.filter((e) => partner.liked.has(e.key) && !mine[e.key]).slice(0, 60);
    if (picks.length) {
      const pickSet = new Set(picks);
      const rest = ordered.filter((e) => !pickSet.has(e));
      const woven: NameEntry[] = [];
      let p = 0;
      let r = 0;
      while (p < picks.length || r < rest.length) {
        if (p < picks.length && (woven.length % 3 === 1 || r >= rest.length)) woven.push(picks[p++]);
        else woven.push(rest[r++]);
      }
      ordered = woven;
    }
  }
  deck.set({ order: ordered.map((e) => e.idx), front, matching, built: Date.now() });
}

/** The next `n` undecided names, front-loaded with any undone cards. */
export function upcoming(core: CoreData, n: number): NameEntry[] {
  const map = decisions.get().map;
  const { order, front } = deck.get();
  const fams = decidedFamilies();
  const pref = settings.get().sex;
  const out: NameEntry[] = [];
  const seen = new Set<string>();
  for (const key of front) {
    const e = core.byKey.get(key);
    if (e && !map[key] && !seen.has(key)) {
      out.push(e);
      seen.add(key);
      if (out.length >= n) return out;
    }
  }
  for (const i of order) {
    const e = core.entries[i];
    if (!map[e.key] && !seen.has(e.key) && !retired(e, fams, pref)) {
      out.push(e);
      seen.add(e.key);
      if (out.length >= n) break;
    }
  }
  return out;
}

/** Decided, or another spelling of it has been: the deck won't deal it. */
export function settled(core: CoreData, key: string): boolean {
  if (decisions.get().map[key]) return true;
  const e = core.byKey.get(key);
  return !!e && retired(e, decidedFamilies(), settings.get().sex);
}

/** another spelling of this name has been decided (a partner's pick still comes up) */
function retired(e: NameEntry, fams: Set<string>, pref: SexPref): boolean {
  if (!fams.size) return false;
  const fam = familyOfKey(e.key, sexIdxFor(e, pref));
  return !!fam && fams.has(fam) && !partnerSets.get().liked.has(e.key);
}

export function remainingCount(core: CoreData): number {
  const map = decisions.get().map;
  const fams = decidedFamilies();
  const pref = settings.get().sex;
  let n = 0;
  for (const i of deck.get().order) {
    const e = core.entries[i];
    if (!map[e.key] && !retired(e, fams, pref)) n++;
  }
  return n;
}

export function bringToFront(key: string): void {
  deck.set((s) => ({ front: [key, ...s.front.filter((k) => k !== key)].slice(0, 20) }));
}
